'use client';

import Markdown, { FootnoteHandler } from '@/app/common/Markdown';
import { FootnoteDefinition } from '@/lib/parseMarkdown';

/**
 * A page-aware footnote handler for the print pipeline, shared by the
 * single-supplement renderer (PrintEssay) and the full-book renderer
 * (PrintScenario).
 *
 * Unlike the on-site `Sidenote` (app/supplements/EssayPage.tsx), which renders
 * only a numbered marker and JS-positions the body in a separate absolutely-
 * positioned column, this renders the FULL note inline as a Tufte `.sidenote`
 * float right where it's referenced. Floats fragment across page boundaries
 * with their paragraph, so the paginator places each note on the page where its
 * reference lands — no JS, no measurement. Numbering comes from tufte.css
 * counters (recolored green in print.css to match AI-2027).
 *
 * Each note renders TWICE: the plain `.sidenote` floats right, and a
 * `.sidenote-left` copy floats left. In the bound-book layout (?book=1) the
 * page content box spans a gutter on BOTH sides of the text column and the
 * spine-side gutter hangs off the paper via negative @page :left/:right
 * margins — so on any given page face exactly one copy is on the paper and
 * the other is cropped (see print.css "Bound-book layout"). Outside book
 * mode the left copy is display:none and costs nothing.
 *
 * Each markdown document (the scenario, each supplement) carries its own
 * footnote definitions, so build a handler per document with its own array.
 */
export function makePrintSidenote(
  footnotes: FootnoteDefinition[],
): FootnoteHandler {
  const PrintSidenote: FootnoteHandler = ({ footnoteReference }) => {
    const definition = footnotes.find(
      (f) => f.identifier === footnoteReference.identifier,
    );
    if (!definition) return null;
    // A `.sidenote` is an inline <span> inside the referencing <p>, so its
    // contents must be inline too. Footnote definitions are block nodes
    // (paragraphs); flatten each to its inline children (nesting block <p>
    // inside the inline span would trigger a hydration mismatch).
    const inlineChildren = definition.children.flatMap((node, i) =>
      node.type === 'paragraph'
        ? i > 0
          ? [{ type: 'text', value: ' ' } as const, ...node.children]
          : node.children
        : [node],
    );
    const body = (
      <Markdown
        nodes={inlineChildren}
        FootnoteHandler={makePrintSidenote(footnotes)}
      />
    );
    return (
      <>
        <label className="sidenote-number" />
        {/* No `markdown-content` class here: it carries
            `counter-reset: sidenote-counter` (tufte.css), which would re-zero
            the counter inside the note so `.sidenote::before` prints 0. Both
            copies read the same counter — the increment lives on the single
            label above. */}
        <span className="sidenote">{body}</span>
        <span className="sidenote sidenote-left">{body}</span>
      </>
    );
  };
  PrintSidenote.displayName = 'PrintSidenote';
  return PrintSidenote;
}
